import {
  AsyncInputSelectControlField,
  SimpleModal,
  type PaginatedResult
} from '@sequencehq/core-components'
import { fetchQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { ContractReference } from 'Cube/domain/cube.domain.types'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { FC, useCallback, useState } from 'react'
import { getReferenceModalCopy } from './contractReferences.helpers'

type Quote = DashboardApi20240730.GetQuotes.Quote

const PAGE_SIZE = 50

export const QuoteReferenceModal: FC<{
  customerId: string
  /** The quote already on the phase, when changing it. */
  initialReference?: ContractReference
  onSubmit: (quote: { id: string; quoteNumber: string }) => void
  onClose: () => void
}> = props => {
  const hook = useQuoteReferenceModal(props)
  const copy = getReferenceModalCopy({
    isChanging: Boolean(props.initialReference),
    noun: 'quote'
  })

  return (
    <SimpleModal
      isOpen
      onClose={props.onClose}
      variant="dialog"
      minHeight="0px"
      title={copy.title}
      submitButtonText={copy.submitButtonText}
      submitDisabled={!hook.isValid}
      onSubmit={hook.submit}
      data-testid="phase.contractReferences.quoteModal"
    >
      <AsyncInputSelectControlField<Quote>
        label="Quote"
        placeholder="Select a quote"
        paginatedFetcher={hook.paginatedFetcher}
        mapToOption={hook.mapToOption}
        resolveValue={hook.resolveValue}
        value={hook.fields.quoteId.value}
        onChange={hook.fields.quoteId.onChange}
        validationErrors={hook.fields.quoteId.validationErrors}
        data-testid="phase.contractReferences.quoteModal.quote"
      />
    </SimpleModal>
  )
}

const useQuoteReferenceModal = (props: {
  customerId: string
  initialReference?: ContractReference
  onSubmit: (quote: { id: string; quoteNumber: string }) => void
}) => {
  /**
   * Quote numbers by id, for every quote the picker has listed so far. The
   * current quote starts in here from its saved label, so it still shows when
   * it isn't on the page the picker has loaded.
   */
  const [quoteNumbers, setQuoteNumbers] = useState<Record<string, string>>(
    props.initialReference
      ? { [props.initialReference.value]: props.initialReference.label }
      : {}
  )

  const { fields, queries } = useForm({
    value: { quoteId: props.initialReference?.value ?? '' },
    fieldConfiguration: [{ property: 'quoteId', validation: [required] }]
  })

  /**
   * No status filter: the backend checks the quote belongs to this account when
   * the schedule saves. The search box only matches quote titles, because that
   * is all `getQuotes` can filter on.
   */
  const paginatedFetcher = useCallback(
    async (
      searchTerm: string,
      cursor?: string
    ): Promise<PaginatedResult<Quote>> => {
      const data = await fetchQuery(
        apiQueryClient,
        dashboard20240730Client.getQuotes,
        {
          customerId: props.customerId,
          title: searchTerm || undefined,
          limit: PAGE_SIZE,
          after: cursor
        }
      )

      const items = data?.items ?? []

      setQuoteNumbers(previous => ({
        ...previous,
        ...Object.fromEntries(items.map(quote => [quote.id, quote.quoteNumber]))
      }))

      return {
        items,
        cursor: data?.pagination.after,
        hasMore: items.length > 0 && Boolean(data?.pagination.after)
      }
    },
    [props.customerId]
  )

  const mapToOption = useCallback(
    // A quote without a title is listed by its number alone.
    (quote: Quote) =>
      quote.title
        ? {
            value: quote.id,
            label: quote.title,
            secondaryLabel: quote.quoteNumber
          }
        : { value: quote.id, label: quote.quoteNumber },
    []
  )

  const resolveValue = useCallback(
    (quoteId: string) =>
      Promise.resolve({
        value: quoteId,
        label: quoteNumbers[quoteId] ?? quoteId
      }),
    [quoteNumbers]
  )

  return {
    fields,
    isValid: queries.isValid,
    paginatedFetcher,
    mapToOption,
    resolveValue,
    submit: () => {
      const quoteId = fields.quoteId.value
      const quoteNumber = quoteNumbers[quoteId]

      if (!quoteNumber) {
        return false
      }

      props.onSubmit({ id: quoteId, quoteNumber })
      return true
    }
  }
}
